import { createContext, useContext, useMemo, useCallback, useRef } from 'react'

// Import custom hooks
import { useHospitalDetail } from './hooks/use-hospital-detail'
import { useHospitalAnalytics } from './hooks/use-hospital-analytics'
import { useHospitalScroll } from './hooks/use-hospital-scroll'

import type { User } from '@/features/member/member.type'
import type {
  HospitalDetailContextValue,
  HospitalDetailDataContextValue,
  HospitalDetailActionsContextValue,
  HospitalDetailModels,
  HospitalDetailAnalytics,
} from './types'

/**
 * Hospital Detail Data Context for sharing static data across components
 */
const HospitalDetailDataContext = createContext<HospitalDetailDataContextValue | null>(null)

/**
 * Hospital Detail Actions Context for sharing analytics and actions across components
 */
const HospitalDetailActionsContext = createContext<HospitalDetailActionsContextValue | null>(null)

/**
 * Combined Context for backward compatibility
 */
const HospitalDetailContext = createContext<HospitalDetailContextValue | null>(null)

/**
 * Hook to access hospital detail data context (for performance-critical components)
 */
export const useHospitalDetailData = () => {
  const context = useContext(HospitalDetailDataContext)
  if (!context) {
    throw new Error('useHospitalDetailData must be used within a HospitalDetailProvider')
  }
  return context
}

/**
 * Hook to access hospital detail actions context (for components that only need analytics)
 */
export const useHospitalDetailActions = () => {
  const context = useContext(HospitalDetailActionsContext)
  if (!context) {
    throw new Error('useHospitalDetailActions must be used within a HospitalDetailProvider')
  }
  return context
}

/**
 * Hook to access hospital detail context (backward compatibility - combines both data and actions)
 */
export const useHospitalDetailContext = () => {
  const context = useContext(HospitalDetailContext)
  if (!context) {
    throw new Error('useHospitalDetailContext must be used within a HospitalDetailProvider')
  }
  return context
}

/**
 * Props for HospitalDetailProvider
 */
interface HospitalDetailProviderProps {
  user: User | null
  models: HospitalDetailModels
  optionalMarketingItems: string[]
  onClickTab: (targetId: string) => void
  onClickMoreList: (target: string) => void
  onClickCopyAddress: () => void
  onClickMap: () => void
  onClickConsultation: () => void
  onClickChatConsultation: () => void
  children: React.ReactNode
}

/**
 * Provider component for Hospital Detail context
 */
export const HospitalDetailProvider = ({
  user,
  models,
  optionalMarketingItems,
  onClickTab,
  onClickMoreList,
  onClickCopyAddress,
  onClickMap,
  onClickConsultation,
  onClickChatConsultation,
  children,
}: HospitalDetailProviderProps) => {
  // Use custom hooks for data processing and analytics
  const processedData = useHospitalDetail(
    models.hospital,
    models.doctors,
    models.events,
    models.reviewWithPagination,
    user,
  )

  const analytics = useHospitalAnalytics(user, models.hospital)

  // Ref management system for scroll targets
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map())
  const sectionRefObjects = useRef<React.RefObject<HTMLElement>[]>([])

  const registerSectionRef = useCallback((sectionId: string, element: HTMLElement | null) => {
    if (element) {
      sectionRefs.current.set(sectionId, element)
    } else {
      sectionRefs.current.delete(sectionId)
    }
  }, [])

  const getSectionRef = useCallback((sectionId: string) => {
    return sectionRefs.current.get(sectionId)
  }, [])

  // Use scroll management hook
  const scrollData = useHospitalScroll(sectionRefObjects.current, (visibleSections) => {
    // Track section visibility for analytics
    if (user && user.id !== 0) {
      visibleSections.forEach((sectionIndex) => {
        const sectionNames = ['review', 'hospitalInfo', 'event']
        const sectionName = sectionNames[sectionIndex]
        if (sectionName) {
          analytics.trackSectionView(sectionName)
        }
      })
    }
  })
  // Enhanced analytics with custom hook integration and event handlers
  const enhancedAnalytics: HospitalDetailAnalytics = useMemo(
    () => ({
      ...analytics,
      trackTabClick: (targetId: string) => {
        onClickTab(targetId)
        analytics.trackTabClick(targetId)
      },
      trackMoreListClick: (target: string) => {
        onClickMoreList(target)
        analytics.trackMoreListClick(target)
      },
      trackCopyAddress: () => {
        onClickCopyAddress()
        analytics.trackCopyAddress()
      },
      trackMapClick: () => {
        onClickMap()
        analytics.trackMapClick()
      },
      trackConsultationClick: () => {
        onClickConsultation()
        analytics.trackConsultationClick()
      },
      trackChatConsultationClick: () => {
        onClickChatConsultation()
        // If analytics hook has this method, call it. Otherwise, just implement logic here if needed or add to hook.
        // analytics.trackChatConsultationClick() // Assuming analytics hook doesn't have it yet.
      },
    }),
    [
      analytics,
      onClickTab,
      onClickMoreList,
      onClickCopyAddress,
      onClickMap,
      onClickConsultation,
      onClickChatConsultation,
    ],
  )

  // Separate data context value for components that only need data
  const dataContextValue: HospitalDetailDataContextValue = useMemo(
    () => ({
      user: processedData.user || null,
      hospital: processedData.hospital,
      doctors: processedData.doctors.activeDoctors,
      events: processedData.events.activeEvents,
      reviews: models.reviewWithPagination, // Use original reviews data
      optionalMarketingItems,
      // Add processed data from custom hooks
      badges: processedData.badges,
      summaryStats: processedData.summaryStats,
      hasVisitedBefore: processedData.hasVisitedBefore,
      canBookAppointment: processedData.canBookAppointment,
      // Add additional processed data
      doctorsBySpecialization: processedData.doctors.doctorsBySpecialization,
      featuredDoctors: processedData.doctors.featuredDoctors,
      totalDoctorsCount: processedData.doctors.totalDoctorsCount,
      sortedEvents: processedData.events.sortedEvents,
      featuredEvents: processedData.events.featuredEvents,
      totalEventsCount: processedData.events.totalEventsCount,
    }),
    [processedData, optionalMarketingItems, models.reviewWithPagination],
  )

  // Separate actions context value for components that only need actions
  const actionsContextValue: HospitalDetailActionsContextValue = useMemo(
    () => ({
      analytics: enhancedAnalytics,
    }),
    [enhancedAnalytics],
  )

  // Combined context value for backward compatibility
  const contextValue: HospitalDetailContextValue = useMemo(
    () => ({
      ...dataContextValue,
      ...actionsContextValue,
      registerSectionRef,
      getSectionRef,
      // Add scroll data
      scrollY: scrollData.scrollY,
      scrollDirection: scrollData.scrollDirection,
      visibleSections: scrollData.visibleSections,
      scrollToSection: scrollData.scrollToSection,
      isNearTop: scrollData.isNearTop,
      isNearBottom: scrollData.isNearBottom,
    }),
    [dataContextValue, actionsContextValue, registerSectionRef, getSectionRef, scrollData],
  )

  return (
    <HospitalDetailDataContext.Provider value={dataContextValue}>
      <HospitalDetailActionsContext.Provider value={actionsContextValue}>
        <HospitalDetailContext.Provider value={contextValue}>{children}</HospitalDetailContext.Provider>
      </HospitalDetailActionsContext.Provider>
    </HospitalDetailDataContext.Provider>
  )
}
